import React, { useEffect, useState, useCallback } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Container, Alert, Modal, Button, Card } from 'react-bootstrap';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { MapMarker, Company } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import MadeWithBadge from './MadeWithBadge';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE';

// Validate and sanitize hex color to prevent injection
const sanitizeColor = (color: string | undefined): string | undefined => {
  if (!color) return undefined;
  const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexColorRegex.test(color) ? color : undefined;
};

const ShareIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5z"/>
  </svg>
);

interface MapComponentProps {
  caseStudies: MapMarker[];
  onMarkerClick: (caseStudy: MapMarker) => void;
  markerColor?: string;
  focusLat?: number;
  focusLng?: number;
  // Nonce published by Navigation when "View Full Map" is clicked. Changes force a re-fit
  // even if the URL and focus params are unchanged.
  fitNonce?: number;
}

const MapComponent: React.FC<MapComponentProps> = ({ caseStudies, onMarkerClick, markerColor, focusLat, focusLng, fitNonce }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<google.maps.Map>();
  const clustererRef = React.useRef<MarkerClusterer | null>(null);

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {
        center: { lat: 0, lng: 0 },
        zoom: 5,
        streetViewControl: false,
        mapId: 'a7ecd8e0022096171640a52b', // Required for Advanced Markers
      }));
    }
  }, [ref, map]);

  React.useEffect(() => {
    if (!map) return;
    let cancelled = false;

    const loadMarkers = async () => {
      const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
      if (cancelled) return;

      // Tear down any previous clusterer before rebuilding.
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
        clustererRef.current = null;
      }

      const bounds = new google.maps.LatLngBounds();
      const markers: google.maps.marker.AdvancedMarkerElement[] = [];

      caseStudies.forEach((caseStudy) => {
        const position = {
          lat: caseStudy.location.coordinates[1],
          lng: caseStudy.location.coordinates[0],
        };

        let pinElement;
        const safeColor = sanitizeColor(markerColor);
        if (safeColor) {
          pinElement = new PinElement({
            background: safeColor,
            borderColor: '#ffffff',
            glyphColor: '#ffffff',
            scale: 1.2,
          });
        }

        const marker = new AdvancedMarkerElement({
          position,
          title: caseStudy.projectName,
          content: pinElement?.element,
        });

        marker.addListener('click', () => {
          onMarkerClick(caseStudy);
        });

        markers.push(marker);
        bounds.extend(position);
      });

      clustererRef.current = new MarkerClusterer({
        map,
        markers,
        // Override default cluster-click zoom so it matches the initial fit: after fitBounds
        // settles, pull back half a zoom level so the outermost pins aren't hugging the edges.
        onClusterClick: (_event, cluster, clusterMap) => {
          if (!cluster.bounds) return;
          clusterMap.fitBounds(cluster.bounds);
          google.maps.event.addListenerOnce(clusterMap, 'idle', () => {
            const m = clusterMap as any;
            m.setZoom(m.getZoom() - 0.5);
          });
        },
      });

      if (focusLat !== undefined && focusLng !== undefined) {
        map.setCenter({ lat: focusLat, lng: focusLng });
        (map as any).setZoom(11);
      } else if (caseStudies.length === 1) {
        // fitBounds on a single point would zoom to max; pull back to country-level instead.
        map.setCenter({
          lat: caseStudies[0].location.coordinates[1],
          lng: caseStudies[0].location.coordinates[0],
        });
        (map as any).setZoom(8);
      } else if (caseStudies.length > 0) {
        map.fitBounds(bounds);
        google.maps.event.addListenerOnce(map, 'idle', () => {
          const m = map as any;
          const z = m.getZoom();
          // Cap at 11 for tight clusters; otherwise pull back half a level so the outermost
          // pins aren't pressed against the edge of the viewport.
          m.setZoom(z > 11 ? 11 : z - 0.5);
        });
      }
    };

    loadMarkers();

    return () => {
      cancelled = true;
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
        clustererRef.current = null;
      }
    };
  }, [map, caseStudies, onMarkerClick, markerColor, focusLat, focusLng, fitNonce]);

  return <div ref={ref} style={{ width: '100%', height: 'calc(100dvh - var(--app-nav-height, 56px))' }} />;
};

const MapView: React.FC = () => {
  const [caseStudies, setCaseStudies] = useState<MapMarker[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<MapMarker | null>(null);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const focusLat = searchParams.get('lat') ? parseFloat(searchParams.get('lat')!) : undefined;
  const focusLng = searchParams.get('lng') ? parseFloat(searchParams.get('lng')!) : undefined;
  const fitNonce = (location.state as { fitNonce?: number } | null)?.fitNonce;

  const getShareUrl = (caseStudy: MapMarker) => {
    const base = window.location.origin;
    return userId
      ? `${base}/${userId}/case-study/${caseStudy._id}`
      : `${base}/case-study/${caseStudy._id}`;
  };

  const handleShare = async () => {
    if (selectedCaseStudy) {
      try {
        await navigator.clipboard.writeText(getShareUrl(selectedCaseStudy));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy URL:', err);
      }
    }
  };

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      const [markerData, companyData] = await Promise.all([
        api.getMapMarkers(userId),
        api.getCompany(userId)
      ]);
      // Backend filters hideFromMap server-side; client filter kept as a cheap belt-and-suspenders
      // for the legacy fallback path.
      setCaseStudies(markerData.filter((cs) => !cs.hideFromMap));
      setCompany(companyData);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkerClick = useCallback((caseStudy: MapMarker) => {
    setActivePhotoIndex(0);
    setSelectedCaseStudy(caseStudy);
  }, []);

  const handleViewDetails = () => {
    if (!selectedCaseStudy) return;
    if (selectedCaseStudy.caseStudyUrl) {
      window.open(selectedCaseStudy.caseStudyUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const url = userId
      ? `/${userId}/case-study/${selectedCaseStudy._id}`
      : `/case-study/${selectedCaseStudy._id}`;
    navigate(url);
  };

  const render = (status: Status) => {
    if (status === Status.LOADING) return <div>Loading Google Maps...</div>;
    if (status === Status.FAILURE) return <Alert variant="danger">Failed to load Google Maps</Alert>;
    return <MapComponent caseStudies={caseStudies} onMarkerClick={handleMarkerClick} markerColor={company?.markerColor} focusLat={focusLat} focusLng={focusLng} fitNonce={fitNonce} />;
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">Loading...</div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (caseStudies.length === 0) {
    const isOwner = auth.isAuthenticated && auth.userId === userId;
    return (
      <Container className="mt-5 text-center">
        <h3>No records to display yet</h3>
        <p className="text-muted">
          {isOwner
            ? 'Your map is empty. Add your first record to see it pinned here.'
            : 'This map doesn\u2019t have any records to show right now. Check back soon.'}
        </p>
        {isOwner && (
          <Link to={userId ? `/${userId}/admin/case-study/new` : '/admin/case-study/new'}>
            <Button variant="primary">Add Your First Record</Button>
          </Link>
        )}
      </Container>
    );
  }

  return (
    <>
      <Container fluid className="p-0" style={{ position: 'relative' }}>
        <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={render} />
        {/* Free-tier attribution on the main shareable map (https://mapofprojects.com/:userId). */}
        {company?.ownerTier === 'free' && <MadeWithBadge variant="floating" />}
      </Container>

      <Modal show={!!selectedCaseStudy} onHide={() => setSelectedCaseStudy(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedCaseStudy?.projectName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCaseStudy?.photos[0] && (
            <div className="mb-3">
              <div style={{ position: 'relative', maxHeight: '300px', width: '100%', overflow: 'hidden' }}>
                <img
                  key={activePhotoIndex}
                  src={selectedCaseStudy.photos[activePhotoIndex]?.url || selectedCaseStudy.photos[0].url}
                  alt={selectedCaseStudy.projectName}
                  className="img-fluid mb-2"
                  style={{
                    maxHeight: '300px',
                    width: '100%',
                    objectFit: 'cover',
                    animation: 'fadeIn 0.3s ease-in-out',
                  }}
                />
              </div>

              {/* Clickable thumbnails */}
              {selectedCaseStudy.photos.length > 1 && (
                <div className="d-flex flex-wrap gap-2 justify-content-center mt-2">
                  {selectedCaseStudy.photos.map((photo, index) => (
                    <img
                      key={index}
                      src={photo.url}
                      alt={`${selectedCaseStudy.projectName} - Photo ${index + 1}`}
                      onClick={() => setActivePhotoIndex(index)}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        border: activePhotoIndex === index ? '2px solid #007bff' : '2px solid #dee2e6',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        opacity: activePhotoIndex === index ? 1 : 0.7,
                        transition: 'border-color 0.2s ease, opacity 0.2s ease',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {selectedCaseStudy?.approximateLocation && (
            <span className="badge bg-warning text-dark mb-2" style={{ fontSize: '0.75rem' }}>
              Approximate location
            </span>
          )}
          {selectedCaseStudy?.country && (
            <Card.Text>
              <strong>Country:</strong> {selectedCaseStudy.country}
            </Card.Text>
          )}
          <Card.Text>
            {selectedCaseStudy?.description.substring(0, 200)}
            {selectedCaseStudy && selectedCaseStudy.description.length > 200 && '...'}
          </Card.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleViewDetails}>
            {selectedCaseStudy?.caseStudyUrl ? 'Visit case study →' : 'View Details'}
          </Button>
          <Button
            variant={copied ? "success" : "outline-secondary"}
            onClick={handleShare}
            title="Copy link to clipboard"
          >
            <ShareIcon /> {copied ? 'Copied!' : 'Share'}
          </Button>
          <Button variant="secondary" onClick={() => setSelectedCaseStudy(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MapView;